import { useMemo, useState } from 'react';
import { useMySpend } from '../hooks/useMySpend';
import { useMyGroups } from '../hooks/useGroups';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { StackedBarChart, type StackedBar } from '../components/charts/StackedBarChart';
import { Sparkline } from '../components/charts/Sparkline';
import { ProgressBar } from '../components/ProgressBar';
import { fmtDateTime, fmtDay, fmtMonth, fmtMonthLong, fmtUsd, fmtUsd0 } from '../lib/format';
import { sortTools, toolColor } from '../lib/toolColors';
import type { MySpendDto, SourceSyncStatusDto } from '../lib/types';

const MONTHS = 6;

export function MySpend() {
  const { data: me } = useCurrentUser();
  const q = useMySpend(MONTHS);

  if (q.isLoading) return <div className="text-slate-500">Loading your spend…</div>;
  if (q.error || !q.data) return <div className="text-red-600 text-sm">Could not load your spend. Try again in a moment.</div>;

  return <MySpendInner data={q.data} displayName={me?.displayName ?? me?.email ?? ''} />;
}

function MySpendInner({ data, displayName }: { data: MySpendDto; displayName: string }) {
  const [showTable, setShowTable] = useState(false);

  const tools = useMemo(
    () => sortTools([...data.mtdByTool.map(t => t.tool), ...data.monthlyTrend.map(m => m.tool)]),
    [data]
  );

  const months = useMemo(() => lastMonths(data.month, MONTHS), [data.month]);

  const bars: StackedBar[] = useMemo(
    () =>
      months.map(m => ({
        key: m,
        label: fmtMonth(m),
        values: Object.fromEntries(
          data.monthlyTrend.filter(r => r.month === m).map(r => [r.tool, r.amountUsd])
        )
      })),
    [months, data.monthlyTrend]
  );

  const monthTotals = useMemo(
    () => Object.fromEntries(months.map(m => [m, data.monthlyTrend.filter(r => r.month === m).reduce((s, r) => s + r.amountUsd, 0)])),
    [months, data.monthlyTrend]
  );
  const prevMonth = months[months.length - 2];
  const prevTotal = prevMonth ? monthTotals[prevMonth] : 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <h1 className="text-2xl font-bold text-atg-navy">My AI spend</h1>
          <p className="text-slate-600 mt-1 text-sm">
            {displayName && <span>{displayName} · </span>}
            {fmtMonthLong(data.month)} to date. Only you and the DevSpend admins can see these numbers.
          </p>
        </div>
        <div className="ml-auto text-xs text-slate-500">
          <FreshnessNote sources={data.sources} />
        </div>
      </div>

      {!data.hasData && (
        <div className="bg-white rounded-lg shadow border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-atg-navy">No spend recorded for you yet</h2>
          <p className="text-sm text-slate-600 mt-2">
            Claude Code usage syncs from Anthropic every morning and lags about a day. Codex arrives from the
            monthly OpenAI leaderboard. If you have been using either tool for more than a couple of days and
            still see nothing here, your work account email may differ from the one your tools are signed in
            with — let the DevSpend admin know.
          </p>
        </div>
      )}

      {/* KPI tiles: month-to-date total, then one per tool in fixed order */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Tile label={`${fmtMonth(data.month)} to date`} value={fmtUsd(data.mtdTotalUsd)} sub={prevMonth ? `${fmtMonth(prevMonth)} total ${fmtUsd(prevTotal)}` : undefined} />
        {tools.map(tool => {
          const mtd = data.mtdByTool.find(t => t.tool === tool)?.amountUsd ?? 0;
          const share = data.mtdTotalUsd > 0 ? Math.round((mtd / data.mtdTotalUsd) * 100) : 0;
          return <Tile key={tool} label={tool} swatch={toolColor(tool)} value={fmtUsd(mtd)} sub={mtd > 0 ? `${share}% of this month` : 'No usage this month'} />;
        })}
      </div>

      <MyGroups />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <section className="bg-white rounded-lg shadow border border-slate-200 p-4 lg:col-span-2">
          <div className="flex items-center gap-3 mb-2">
            <h2 className="text-sm font-semibold text-slate-800">Monthly spend, last {MONTHS} months</h2>
            <button onClick={() => setShowTable(s => !s)} className="ml-auto text-xs text-atg-blue hover:underline">
              {showTable ? 'Show chart' : 'Show table'}
            </button>
          </div>
          {showTable ? (
            <TrendTable months={months} tools={tools} data={data} />
          ) : (
            <>
              <StackedBarChart bars={bars} series={tools} colorOf={toolColor} formatValue={fmtUsd0} ariaLabel={`Monthly AI spend by tool for the last ${MONTHS} months`} />
              {tools.length > 1 && (
                <ul className="mt-2 flex flex-wrap gap-4 text-xs text-slate-600">
                  {tools.map(t => (
                    <li key={t} className="flex items-center gap-1.5">
                      <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: toolColor(t) }} />
                      {t}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </section>

        <section className="bg-white rounded-lg shadow border border-slate-200 p-4">
          <h2 className="text-sm font-semibold text-slate-800 mb-2">Daily spend, {fmtMonth(data.month)}</h2>
          {data.dailyCurrentMonth.length === 0 ? (
            <p className="text-xs text-slate-500">No daily data yet this month.</p>
          ) : (
            <Sparkline
              points={data.dailyCurrentMonth.map(d => ({ key: d.date, label: fmtDay(d.date), value: d.amountUsd }))}
              color="#3560c0"
              formatValue={fmtUsd}
              ariaLabel="Daily AI spend for the current month"
            />
          )}
          <p className="text-xs text-slate-500 mt-2">
            Claude Code is daily; Codex lands as a monthly figure on the 1st, so it does not move day to day.
          </p>
        </section>
      </div>
    </div>
  );
}

/** Groups the developer belongs to: the group's total and target progress, never teammates' numbers. */
function MyGroups() {
  const q = useMyGroups();
  if (!q.data || q.data.length === 0) return null;
  return (
    <section className="bg-white rounded-lg shadow border border-slate-200 p-4">
      <h2 className="text-sm font-semibold text-slate-800 mb-1">My groups</h2>
      <p className="text-xs text-slate-500 mb-3">
        Team totals against the monthly target. Everyone in a group gets an email when it crosses a threshold.
      </p>
      <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {q.data.map(g => (
          <li key={g.id} className="border border-slate-200 rounded-md p-3 space-y-2">
            <div className="flex items-baseline gap-2">
              <span className="font-medium text-slate-800">{g.name}</span>
              <span className="text-xs text-slate-500">{g.memberCount} members</span>
              <span className="ml-auto tabular-nums text-sm">
                {fmtUsd(g.mtdUsd)}{g.targetUsd !== null && <span className="text-slate-500"> / {fmtUsd(g.targetUsd)}</span>}
              </span>
            </div>
            <ProgressBar pct={g.pct} thresholds={g.thresholds} />
            <div className="flex text-xs text-slate-500">
              <span>{g.pct !== null ? `${g.pct}% of target used` : 'No target set'}</span>
              {g.lastNotifiedPct !== null && <span className="ml-auto text-atg-orange">Alert sent at {g.lastNotifiedPct}%</span>}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Tile({ label, value, sub, swatch }: { label: string; value: string; sub?: string; swatch?: string }) {
  return (
    <div className="bg-white rounded-lg shadow border border-slate-200 p-4">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500">
        {swatch && <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: swatch }} />}
        {label}
      </div>
      <div className="text-2xl font-semibold text-slate-900 mt-1 tabular-nums">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function TrendTable({ months, tools, data }: { months: string[]; tools: string[]; data: MySpendDto }) {
  const cell = (m: string, t: string) => data.monthlyTrend.find(r => r.month === m && r.tool === t)?.amountUsd ?? 0;
  return (
    <table className="w-full text-sm">
      <thead className="bg-slate-100 text-left">
        <tr>
          <th className="px-3 py-2">Month</th>
          {tools.map(t => <th key={t} className="px-3 py-2 text-right">{t}</th>)}
          <th className="px-3 py-2 text-right">Total</th>
        </tr>
      </thead>
      <tbody>
        {months.map(m => (
          <tr key={m} className="border-t border-slate-200">
            <td className="px-3 py-2">{fmtMonth(m)}</td>
            {tools.map(t => <td key={t} className="px-3 py-2 text-right tabular-nums">{fmtUsd(cell(m, t))}</td>)}
            <td className="px-3 py-2 text-right tabular-nums font-medium">{fmtUsd(tools.reduce((s, t) => s + cell(m, t), 0))}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function FreshnessNote({ sources }: { sources: SourceSyncStatusDto[] }) {
  if (sources.length === 0) return <span>No data synced yet.</span>;
  return (
    <span>
      {sources.map((s, i) => (
        <span key={s.source}>
          {i > 0 && ' · '}
          {sourceLabel(s.source)} {s.status === 'Succeeded' ? `as of ${fmtDateTime(s.finishedAt)}` : s.status === 'Running' ? 'syncing…' : `last sync failed ${fmtDateTime(s.finishedAt)}`}
        </span>
      ))}
    </span>
  );
}

function sourceLabel(source: string): string {
  switch (source) {
    case 'ClaudeEnterprise': return 'Claude';
    case 'ClaudePlatform': return 'Claude API';
    default: return source;
  }
}

/** ["2026-04", …, "2026-09"] ending at `month`. */
function lastMonths(month: string, count: number): string[] {
  const [y, m] = month.split('-').map(Number);
  const out: string[] = [];
  for (let i = count - 1; i >= 0; i--) {
    const d = new Date(Date.UTC(y, m - 1 - i, 1));
    out.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`);
  }
  return out;
}
